"use client";

import { MapPin, ShieldCheck } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const serviceTags = ["First Nation Communities", "Northern Organizations", "Funding Accountability", "Year-End Reporting"];
export default function AboutIntroduction() {
  const {
    ref: textRef,
    isVisible: isTextVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: imgRef,
    isVisible: isImgVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="257364" id="introduction" className="bg-white text-foreground py-16 md:py-24 overflow-hidden border-b border-border/40">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div ref={textRef} className={`max-w-2xl transition-all duration-700 ease-out ${isTextVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="h-1 w-12 bg-secondary-500 mb-6 rounded-full" />

            <p className="text-secondary-700 font-semibold tracking-[0.18em] uppercase text-xs mb-4 font-default">
              Our Mission and Impact
            </p>

            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-primary-600 mb-6 font-default leading-tight tracking-tight">
              Work that helps organizations move forward.
            </h2>

            <div className="space-y-5 text-muted-foreground text-lg leading-relaxed mb-8 font-default">
              <p>
                RiverBend supports First Nation communities and Northern
                organizations with practical financial management, reporting,
                funding accountability, project support, and stronger internal
                systems.
              </p>

              <p>
                This is meaningful work for people who want to solve real
                problems, build useful tools, and work directly with clients.
                Team members contribute ideas, help organize complex
                information, and support practical improvements that can make a
                lasting difference.
              </p>
            </div>

            <div className="flex flex-wrap gap-3">
              {serviceTags.map((tag, i) => <Badge key={i} data-index={i} variant="secondary" className="bg-secondary-50 text-secondary-700 hover:bg-secondary-100 border border-secondary-100 px-4 py-2.5 text-sm font-medium rounded-full flex items-center gap-2 transition-colors">
                  <MapPin className="w-4 h-4 shrink-0" />
                  {tag}
                </Badge>)}
            </div>
          </div>

          <div ref={imgRef} className={`relative transition-all duration-700 delay-200 ease-out ${isImgVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"}`}>
            <div className="relative max-w-xl mx-auto lg:ml-auto lg:mr-0">
              <div className="absolute inset-0 bg-secondary-500/10 rounded-3xl translate-x-4 translate-y-4 -z-10" />

              <div className="relative z-10 rounded-3xl overflow-hidden shadow-xl border border-border/60 bg-white p-3">
                <img src="https://stable-woodcock.10web.cloud/wp-content/uploads/2026/05/ChatGPT-Image-May-16-2026-10_40_37-AM.png" alt="Collaborative team discussion in a warm Northern office setting" className="w-full aspect-[4/3] object-cover rounded-2xl" loading="lazy" />
              </div>

              <div className="absolute left-6 right-6 -bottom-6 sm:left-auto sm:right-6 z-20 bg-white rounded-2xl shadow-xl p-5 flex items-center gap-4 border border-border max-w-[300px]">
                <div className="bg-secondary-100 p-3 rounded-xl text-secondary-700 shrink-0">
                  <ShieldCheck className="w-6 h-6" />
                </div>

                <div>
                  <p className="font-semibold text-primary-600 font-default text-sm leading-tight">
                    Practical Work
                  </p>

                  <p className="text-muted-foreground text-xs mt-1 font-default">
                    Real clients. Real progress.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}